// Account detail page — Overview / Balances / Transactions / Snapshots /
// Mapping History / Status History / Note History tabs.
//
// Balances and Transactions tabs render an empty-state message until Track 3
// fills in the manual-entry + transaction surfaces; Snapshots tab does the
// same until Track 4 lands. The data is real — the messaging just covers the
// case where Tracks 3/4 haven't seeded their flows.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { accounts } from '@/lib/api';
import { formatBalance, formatTransaction } from '@/lib/format';
import { useMe } from '@/hooks/useMe';
import { cn } from '@/lib/cn';
import { HardDeleteButton } from '@/components/HardDeleteButton';
import type { HardDeleteMode } from '@frt/shared-types';
import { ACCOUNT_NOTE_MAX, formatAddress, formatPhone, platformLabel } from '@frt/shared-types';
import { OobTable } from '../profiles/pages';
import { LinkedAccountsPanel } from './linked-accounts';

type Tab =
  | 'overview'
  | 'balances'
  | 'transactions'
  | 'snapshots'
  | 'mapping'
  | 'status'
  | 'notes';

export function AccountDetailPage() {
  const { id: idStr } = useParams({ strict: false }) as { id?: string };
  const id = Number(idStr);
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const canEdit = me?.role === 'admin' || me?.role === 'senior';
  const canSuppress = me?.role === 'admin';
  const [tab, setTab] = useState<Tab>('overview');
  const [showLink, setShowLink] = useState(false);
  const [tillerId, setTillerId] = useState('');
  const [tillerRef, setTillerRef] = useState('');

  const { data: account, isLoading, error } = useQuery({
    queryKey: ['account', id],
    queryFn: () => accounts.get(id),
    enabled: Number.isFinite(id),
  });

  const moveToManual = useMutation({
    mutationFn: () => accounts.moveToManual(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account', id] }),
  });
  const moveToTiller = useMutation({
    mutationFn: () => accounts.moveToTiller(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account', id] }),
  });
  const linkTiller = useMutation({
    mutationFn: () =>
      accounts.linkTiller(id, {
        tiller_account_id: tillerId.trim(),
        tiller_account_reference: tillerRef.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['account', id] });
      void queryClient.invalidateQueries({ queryKey: ['account-mapping', id] });
      setShowLink(false);
      setTillerId('');
      setTillerRef('');
    },
  });
  const unlinkTiller = useMutation({
    mutationFn: () => accounts.unlinkTiller(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['account', id] });
      void queryClient.invalidateQueries({ queryKey: ['account-mapping', id] });
    },
  });
  const suppress = useMutation({
    mutationFn: (status: 'suppressed' | 'closed' | 'converted_personal') =>
      accounts.suppress(id, { account_status: status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account', id] }),
  });
  const restore = useMutation({
    mutationFn: () => accounts.restore(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account', id] }),
  });
  const navigate = useNavigate();
  const deleteAccount = useMutation({
    mutationFn: (body: { mode: HardDeleteMode; reason: string | null }) =>
      accounts.remove(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void navigate({ to: '/accounts' });
    },
  });

  if (!Number.isFinite(id)) return <div className="text-sm text-red-600">Invalid id</div>;
  if (isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (error) return <div className="text-sm text-red-600">{(error as Error).message}</div>;
  if (!account) return <div className="text-sm text-slate-500">Not found</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold text-slate-900 font-mono">
          {account.display_name}
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" onClick={() => setTab('notes')}>
            Add note
          </Button>
          {canEdit && (
            <Link to="/accounts/$id/edit" params={{ id: String(id) }}>
              <Button variant="secondary">Edit</Button>
            </Link>
          )}
          {canEdit &&
            (account.tiller_status === 'manual' ? (
              <Button onClick={() => moveToTiller.mutate()}>Move to Tiller</Button>
            ) : (
              <Button variant="secondary" onClick={() => moveToManual.mutate()}>
                Move to Manual
              </Button>
            ))}
          {canEdit && (
            <Button variant="secondary" onClick={() => setShowLink((v) => !v)}>
              {account.tiller_account_id ? 'Re-link Tiller' : 'Link Tiller'}
            </Button>
          )}
          {canEdit && account.tiller_account_id && (
            <Button
              variant="destructive"
              onClick={() => {
                if (confirm('Unlink the Tiller account?')) unlinkTiller.mutate();
              }}
            >
              Unlink Tiller
            </Button>
          )}
          {canSuppress &&
            (account.account_status === 'active' ? (
              <Button
                variant="destructive"
                onClick={() => {
                  const choice = prompt(
                    'Set status to: suppressed | closed | converted_personal',
                    'suppressed',
                  );
                  if (
                    choice === 'suppressed' ||
                    choice === 'closed' ||
                    choice === 'converted_personal'
                  ) {
                    suppress.mutate(choice);
                  }
                }}
              >
                Suppress
              </Button>
            ) : (
              <Button onClick={() => restore.mutate()}>Restore</Button>
            ))}
          {me?.role === 'admin' && (
            <HardDeleteButton
              entityLabel="account"
              onConfirm={(body) => deleteAccount.mutate(body)}
            />
          )}
        </div>
      </div>

      {showLink && (
        <Card>
          <CardHeader>
            <CardTitle>Link Tiller account</CardTitle>
          </CardHeader>
          <CardBody>
            <form
              className="flex flex-wrap gap-3 items-end"
              onSubmit={(e) => {
                e.preventDefault();
                if (tillerId.trim()) linkTiller.mutate();
              }}
            >
              <label className="block text-sm flex-1 min-w-[16rem]">
                <span className="block text-slate-600">Tiller account_id</span>
                <Input
                  value={tillerId}
                  onChange={(e) => setTillerId(e.target.value)}
                  placeholder="paste from Tiller"
                  required
                />
              </label>
              <label className="block text-sm flex-1 min-w-[16rem]">
                <span className="block text-slate-600">Tiller reference (optional)</span>
                <Input
                  value={tillerRef}
                  onChange={(e) => setTillerRef(e.target.value)}
                />
              </label>
              <Button type="submit" disabled={linkTiller.isPending}>
                Save
              </Button>
            </form>
            {linkTiller.error && (
              <div className="mt-2 text-sm text-red-600">
                {(linkTiller.error as Error).message}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <div className="flex gap-2 border-b border-slate-200">
        <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>
          Overview
        </TabButton>
        <TabButton active={tab === 'balances'} onClick={() => setTab('balances')}>
          Balances
        </TabButton>
        <TabButton active={tab === 'transactions'} onClick={() => setTab('transactions')}>
          Transactions
        </TabButton>
        <TabButton active={tab === 'snapshots'} onClick={() => setTab('snapshots')}>
          Snapshots
        </TabButton>
        <TabButton active={tab === 'mapping'} onClick={() => setTab('mapping')}>
          Mapping History
        </TabButton>
        <TabButton active={tab === 'status'} onClick={() => setTab('status')}>
          Status History
        </TabButton>
        <TabButton active={tab === 'notes'} onClick={() => setTab('notes')}>
          Note History
        </TabButton>
      </div>

      {tab === 'overview' && <OverviewTab account={account} />}
      {tab === 'balances' && <BalancesTab id={id} tillerStatus={account.tiller_status} />}
      {tab === 'transactions' && <TransactionsTab id={id} tillerStatus={account.tiller_status} />}
      {tab === 'snapshots' && <SnapshotsTab id={id} />}
      {tab === 'mapping' && <MappingTab id={id} />}
      {tab === 'status' && <StatusTab id={id} />}
      {tab === 'notes' && <NotesTab id={id} />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        '-mb-px border-b-2 px-3 py-2 text-sm font-medium',
        active
          ? 'border-brand-600 text-brand-700'
          : 'border-transparent text-slate-600 hover:text-slate-900',
      )}
    >
      {children}
    </button>
  );
}

function yesNoOrDash(v: boolean | null | undefined): string {
  return v === true ? 'Yes' : v === false ? 'No' : '—';
}

function OverviewTab({
  account,
}: {
  account: import('@frt/shared-types').AccountDetail;
}) {
  const { data: me } = useMe();
  return (
    <Card>
      <CardBody className="space-y-5">
        <OverviewSection title="Online Profile">
          <Item label="Profile">
            {account.customer_profile ? (
              <Link
                to="/profiles/$id"
                params={{ id: String(account.customer_profile.id) }}
                className="text-brand-700 hover:underline"
              >
                {account.customer_profile.display}
              </Link>
            ) : (
              <span className="text-amber-700">None yet — assign one on the next edit</span>
            )}
          </Item>
          <Item label="Platform">
            {account.platform_name
              ? platformLabel({ name: account.platform_name, type: account.platform_type_name ?? '' })
              : '—'}
          </Item>
          <Item label="Tester">
            <Link
              to="/testers/$id"
              params={{ id: String(account.tester.id) }}
              className="text-brand-700 hover:underline"
            >
              {account.tester_display}
            </Link>
          </Item>
          <Item label="On point tester">
            {account.on_point_tester
              ? account.on_point_tester_display ?? `tester#${account.on_point_tester.id}`
              : '—'}
          </Item>
          <Item label="Phone on file" mono>
            {account.phone_on_file_id ? (
              <Link
                to="/assets/phones/$id"
                params={{ id: String(account.phone_on_file_id) }}
                className="text-brand-700 hover:underline"
              >
                {formatPhone(account.phone_on_file_country_code, account.phone_on_file_number)}
              </Link>
            ) : (
              '—'
            )}
          </Item>
          <Item label="Email on file">{account.email_on_file ?? '—'}</Item>
          <Item label="Address">
            {(account.customer_profile && formatAddress(account.customer_profile)) || '—'}
          </Item>
          <Item label="Tester relationship">
            {account.tester_relationship === 'third_party' ? 'Third Party' : 'Internal'}
          </Item>
        </OverviewSection>

        <OverviewSection title="Account Details">
          <Item label="FRT Customer">
            <Link
              to="/customers/$id"
              params={{ id: String(account.frt_customer.id) }}
              className="text-brand-700 hover:underline"
            >
              {account.frt_customer.code} ({account.frt_customer.name})
            </Link>
          </Item>
          <Item label="Category">{account.frt_category?.name ?? '—'}</Item>
          <Item label="Account type">{account.account_type.code}</Item>
          <Item label="Product type">{account.product_type.code}</Item>
          <Item label="Last 4" mono>
            {account.account_number_last4}
          </Item>
          <Item label="Date opened">{account.date_opened ?? '—'}</Item>
          <Item label="Account status">{account.account_status}</Item>
          <Item label="Latest balance">
            {account.latest_balance_amount !== null
              ? `${formatBalance(account.latest_balance_amount)} (as of ${account.latest_balance_as_of})`
              : '—'}
          </Item>
          <Item label="Accounts held">{account.accounts_held ?? '—'}</Item>
          <Item label="Legacy ID" mono>
            {account.legacy_account_id ?? '—'}
          </Item>
        </OverviewSection>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-800">Account Access</h3>
          <p className="text-xs text-slate-500">OTP channels from the online profile.</p>
          <div className="overflow-x-auto">
            <OobTable entries={account.oob_entries} />
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">
            <Item label="In Bitwarden">{yesNoOrDash(account.in_bitwarden)}</Item>
          </dl>
        </section>

        <OverviewSection title="Payment Methods">
          <Item label="Debit card available">{yesNoOrDash(account.debit_card_available)}</Item>
          <Item label="Checks available">{yesNoOrDash(account.checks_available)}</Item>
        </OverviewSection>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-800">Linked Accounts</h3>
          <LinkedAccountsPanel
            accountId={account.id}
            canEdit={me?.role === 'admin' || me?.role === 'senior'}
            isAdmin={me?.role === 'admin'}
          />
        </section>

        <OverviewSection title="Account Data Linkage">
          <Item label="Tiller status">{account.tiller_status}</Item>
          <Item label="Tiller account_id" mono>
            {account.tiller_account_id ?? '—'}
          </Item>
          <Item label="Tiller reference">{account.tiller_account_reference ?? '—'}</Item>
        </OverviewSection>

        {account.notes && (
          <div>
            <div className="text-slate-500 text-xs">Notes</div>
            <div className="whitespace-pre-wrap text-sm">{account.notes}</div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function OverviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">{children}</dl>
    </section>
  );
}

function Item({
  label,
  mono,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-2 gap-x-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className={mono ? 'font-mono' : undefined}>{children}</dd>
    </div>
  );
}

function BalancesTab({ id, tillerStatus }: { id: number; tillerStatus: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['account-balances', id],
    queryFn: () => accounts.balances(id, { per_page: 100 }),
  });
  return (
    <Card>
      <CardBody>
        {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
        {isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (data?.data.length ?? 0) === 0 ? (
          <div className="text-sm text-slate-500">
            No balances yet.{' '}
            {tillerStatus === 'manual'
              ? 'Use Manual Entry (Track 3) to record balances.'
              : 'Awaiting next Tiller upload (Track 2).'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-3">As of</th>
                <th className="py-1 pr-3">Amount</th>
                <th className="py-1 pr-3">Source</th>
                <th className="py-1 pr-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((b) => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="py-1 pr-3">{b.as_of_date}</td>
                  <td className="py-1 pr-3 font-mono text-right">{formatBalance(b.amount)}</td>
                  <td className="py-1 pr-3">{b.source}</td>
                  <td className="py-1 pr-3">{b.notes ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function TransactionsTab({ id, tillerStatus }: { id: number; tillerStatus: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['account-transactions', id],
    queryFn: () => accounts.transactions(id, { per_page: 100 }),
  });
  return (
    <Card>
      <CardBody>
        {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
        {isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (data?.data.length ?? 0) === 0 ? (
          <div className="text-sm text-slate-500">
            No transactions.{' '}
            {tillerStatus !== 'integrated'
              ? 'Manual-pool accounts have no transaction data.'
              : 'Awaiting next Tiller upload (Track 2).'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-3">Posted</th>
                <th className="py-1 pr-3">Description</th>
                <th className="py-1 pr-3">Amount</th>
                <th className="py-1 pr-3">Category</th>
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((tx) => (
                <tr key={tx.id} className="border-t border-slate-100">
                  <td className="py-1 pr-3">{tx.posted_date}</td>
                  <td className="py-1 pr-3">{tx.description ?? ''}</td>
                  <td className="py-1 pr-3 font-mono text-right">{formatTransaction(tx.amount)}</td>
                  <td className="py-1 pr-3">{tx.category ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function SnapshotsTab({ id }: { id: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['account-snapshots', id],
    queryFn: () => accounts.snapshots(id),
  });
  return (
    <Card>
      <CardBody>
        {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
        {isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (data?.data.length ?? 0) === 0 ? (
          <div className="text-sm text-slate-500">
            No snapshots yet — periods are frozen via Snapshots (Track 4).
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-3">Period</th>
                <th className="py-1 pr-3">Balance</th>
                <th className="py-1 pr-3">Source</th>
                <th className="py-1 pr-3">Frozen at</th>
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="py-1 pr-3 font-mono">{s.period}</td>
                  <td className="py-1 pr-3 font-mono text-right">{formatBalance(s.balance_amount)}</td>
                  <td className="py-1 pr-3">{s.balance_source}</td>
                  <td className="py-1 pr-3 text-xs">{s.frozen_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function MappingTab({ id }: { id: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['account-mapping', id],
    queryFn: () => accounts.mappingHistory(id),
  });
  return (
    <Card>
      <CardBody>
        {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
        {isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (data?.data.length ?? 0) === 0 ? (
          <div className="text-sm text-slate-500">No mapping history yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-3">When</th>
                <th className="py-1 pr-3">Reason</th>
                <th className="py-1 pr-3">From</th>
                <th className="py-1 pr-3">To</th>
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((m) => (
                <tr key={m.id} className="border-t border-slate-100">
                  <td className="py-1 pr-3 text-xs">{m.changed_at}</td>
                  <td className="py-1 pr-3">{m.reason}</td>
                  <td className="py-1 pr-3 font-mono">{m.prior_tiller_account_id ?? '—'}</td>
                  <td className="py-1 pr-3 font-mono">{m.new_tiller_account_id ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

// Note History (mig 0016): add a note, then every note newest first with
// who wrote it and when. Notes are append-only.
function NotesTab({ id }: { id: number }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['account-notes', id],
    queryFn: () => accounts.notes(id),
  });
  const add = useMutation({
    mutationFn: () => accounts.addNote(id, draft.trim()),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['account-notes', id] });
    },
  });
  const notes = data?.data ?? [];
  return (
    <Card>
      <CardBody className="space-y-4">
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim()) add.mutate();
          }}
        >
          <label className="block text-sm">
            <span className="block text-slate-600">Add a note</span>
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={ACCOUNT_NOTE_MAX}
              rows={3}
              className="block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              placeholder="What happened, what was agreed, what to follow up on…"
            />
          </label>
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={!draft.trim() || add.isPending}>
              {add.isPending ? 'Saving…' : 'Save note'}
            </Button>
            {add.error && (
              <span className="text-sm text-red-600">{(add.error as Error).message}</span>
            )}
          </div>
        </form>

        {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
        {isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : notes.length === 0 ? (
          <div className="text-sm text-slate-500">No notes yet.</div>
        ) : (
          <ol className="divide-y divide-slate-100 border-t border-slate-100">
            {notes.map((n) => (
              <li key={n.id} className="py-3">
                <div className="text-xs text-slate-500">
                  {new Date(n.created_at).toLocaleString()} ·{' '}
                  {n.created_by_username ?? 'unknown user'}
                </div>
                <div className="whitespace-pre-wrap text-sm text-slate-800">{n.body}</div>
              </li>
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}

function StatusTab({ id }: { id: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['account-status', id],
    queryFn: () => accounts.statusHistory(id),
  });
  return (
    <Card>
      <CardBody>
        {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
        {isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (data?.data.length ?? 0) === 0 ? (
          <div className="text-sm text-slate-500">No status events yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-3">When</th>
                <th className="py-1 pr-3">Tiller</th>
                <th className="py-1 pr-3">Account</th>
                <th className="py-1 pr-3">Reason</th>
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="py-1 pr-3 text-xs">{e.at}</td>
                  <td className="py-1 pr-3">
                    {e.prior_tiller_status ?? '—'} → {e.new_tiller_status ?? '—'}
                  </td>
                  <td className="py-1 pr-3">
                    {e.prior_account_status ?? '—'} → {e.new_account_status ?? '—'}
                  </td>
                  <td className="py-1 pr-3">{e.reason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}
